import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastrService } from 'ngx-toastr';
import { copyQrImageToClipboard, generateQrDataUrl } from './qr-code.util';

export interface QrCodeDialogData {
  url: string;
  title?: string;
}

@Component({
  selector: 'lib-qr-code-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './qr-code-dialog.component.html',
  styleUrls: ['./qr-code-dialog.component.scss'],
})
export class QrCodeDialogComponent implements OnInit {
  qrCodeDataUrl = '';
  isGenerating = true;
  generateError = false;

  constructor(
    public dialogRef: MatDialogRef<QrCodeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: QrCodeDialogData,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.generateQrCode();
  }

  private async generateQrCode(): Promise<void> {
    if (!this.data?.url) {
      this.isGenerating = false;
      this.generateError = true;
      return;
    }

    try {
      this.qrCodeDataUrl = await generateQrDataUrl(this.data.url);
      this.generateError = false;
    } catch {
      this.generateError = true;
      this.toastr.error('Failed to generate QR code.', 'Error');
    } finally {
      this.isGenerating = false;
    }
  }

  copyLink(): void {
    const url = this.data?.url;
    if (!url) {
      return;
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        this.toastr.success('URL copied to clipboard!', 'Success', {
          timeOut: 2000,
          positionClass: 'toast-top-right',
        });
      }).catch(() => this.fallbackCopy(url));
      return;
    }

    this.fallbackCopy(url);
  }

  private fallbackCopy(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.toastr.success('URL copied to clipboard!', 'Success', {
          timeOut: 2000,
          positionClass: 'toast-top-right',
        });
      } else {
        this.toastr.error('Failed to copy URL. Please copy manually.', 'Error');
      }
    } catch {
      this.toastr.error('Failed to copy URL. Please copy manually.', 'Error');
    } finally {
      document.body.removeChild(textArea);
    }
  }

  async copyQrCode(): Promise<void> {
    if (!this.qrCodeDataUrl) {
      return;
    }

    try {
      await copyQrImageToClipboard(this.qrCodeDataUrl);
      this.toastr.success('QR code copied to clipboard!', 'Success', {
        timeOut: 2000,
        positionClass: 'toast-top-right',
      });
    } catch {
      this.toastr.error('Failed to copy QR code. Please try again.', 'Error');
    }
  }
}
